import { classnames } from '@/helpers';
import { CalendarDayProps } from './CalendarDay.types';

/**
 * A single day cell in the `_CVPickTimeCalendar` month grid (MPA-2581, epic
 * MPA-2598).
 *
 * Figma: Iteration 10 — `_CVPickTimeCalendar` (502-22208). Available days are
 * shown in the link colour and unavailable / past days disabled. Selecting a day
 * navigates to the week view, so the cell carries no selected state. Purely
 * presentational: the state and whether the cell is selectable come from its
 * props.
 */
const CalendarDay = ({
  dayNumber,
  state,
  isToday = false,
  selectable = false,
  ariaLabel,
  onClick,
  className,
}: CalendarDayProps) => {
  // Campaign days render like available days — the month-grid Figma (502-22208)
  // shows no distinct campaign treatment, so the campaign only affects pricing
  // and the checkout handoff, not the day cell. (The week-view DayHeader does
  // surface a campaign sub-label, per its own Figma variant set.)
  const numberColor = state === 'available' || state === 'campaign' ? 'text-fg_link' : 'text-fg_disabled_light';

  // 36px day-number circle inside a 48px cell — Figma _CVPickTimeCalendar (502-22208, _CalendarItem = 48px).
  // height isn't on the token scale in this Tailwind config and 36px has no token, so both use arbitrary px.
  const circle = (
    <span
      className={classnames(
        'text-l flex h-[36px] w-[36px] items-center justify-center rounded-full',
        isToday && 'font-bold',
        numberColor,
      )}>
      {dayNumber}
    </span>
  );

  if (!selectable) {
    return (
      <span
        aria-label={ariaLabel}
        aria-disabled="true"
        className={classnames('flex h-[48px] w-full items-center justify-center', className)}>
        {circle}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={classnames('flex h-[48px] w-full cursor-pointer items-center justify-center', className)}>
      {circle}
    </button>
  );
};

export default CalendarDay;
